<template>
  <div class="achievements-container rounded-4 shadow-sm p-4 bg-white">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h1 class="mb-0"><i class="fas fa-trophy me-2"></i>Mes réalisations</h1>
      <button class="btn btn-primary rounded-pill" @click="showAddModal">
        <i class="fas fa-plus me-1"></i> Ajouter une réalisation
      </button>
    </div>

    <div class="search-filter mb-4 bg-light p-3 rounded-3">
      <div class="row">
        <div class="col-md-6">
          <div class="input-group">
            <span class="input-group-text bg-white"><i class="fas fa-search"></i></span>
            <input v-model="searchQuery" type="text" class="form-control" placeholder="Rechercher un projet...">
          </div>
        </div>
        <div class="col-md-6">
          <select v-model="sortOption" class="form-select">
            <option value="recent">Date récente</option>
            <option value="oldest">Date ancienne</option>
            <option value="name">Nom de projet</option>
          </select>
        </div>
      </div>
    </div>

    <div class="projects-list">
      <div v-if="filteredProjects.length === 0" class="alert alert-info rounded-3">
        <i class="fas fa-info-circle me-2"></i>Aucune réalisation trouvée
      </div>

      <div v-for="(project, index) in filteredProjects" :key="project.id"
        class="project-card mb-4 p-4 rounded-4 bg-light position-relative">
        <div class="project-actions position-absolute top-0 end-0 mt-3 me-3">
          <a v-if="project.link" :href="project.link" target="_blank"
              class="btn btn-sm btn-outline-primary rounded-pill me-2">
              <i class="fas fa-external-link-alt me-1"></i> Voir
            </a>
          <button class="btn btn-sm btn-outline-primary me-1" @click="editProject(index)">
            <i class="fas fa-edit"></i>
          </button>
          <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(index)">
            <i class="fas fa-trash-alt"></i>
          </button>
        </div>

        <div class="row">
          <div class="col-md-3">
            <div class="project-image rounded-3 bg-white p-3 text-center shadow-sm">
              <img v-if="project.image_url" :src="project.image_url" class="p-0 img-fluid"/>
              <i v-else class="fa-3x text-primary fas" :class="project.icon || 'fa-project-diagram'"></i>
            </div>
          </div>
          <div class="col-md-6">
            <h3>{{ project.name }}</h3>
            <p class="text-muted">{{ project.description }}</p>
            <div class="badges">
              <span class="badge bg-primary me-2 rounded-pill">
                <i class="fas fa-calendar me-1"></i> {{ formatDate(project.startDate) }} - {{ project.endDate ?
                  formatDate(project.endDate) : 'En cours' }}
              </span>
              <span class="rounded-pill badge" :class="statusClass(project.status)">
                <i class="fas me-1" :class="statusIcon(project.status)"></i> {{ project.status }}
              </span>
            </div>
            <div class="mt-3" v-if="project.skills && project.skills.length > 0">
              <span class="badge bg-secondary me-1 mb-1 rounded-pill" v-for="(skill, i) in project.skills" :key="i">
                {{ skill }}
              </span>
            </div>
          </div>
          <div class="col-md-3 text-end">
            
            <!-- <button class="btn btn-primary rounded-pill" @click="downloadProject(index)">
                <i class="fas fa-download me-1"></i> Télécharger
              </button> -->
          </div>
        </div>
      </div>
    </div>

    <nav aria-label="Projects pagination" class="mt-4" v-if="filteredProjects.length > 0">
      <ul class="pagination justify-content-center">
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <a class="page-link rounded-start" href="#" @click.prevent="prevPage">Précédent</a>
        </li>
        <li class="page-item" v-for="page in totalPages" :key="page" :class="{ active: page === currentPage }">
          <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
        </li>
        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <a class="page-link rounded-end" href="#" @click.prevent="nextPage">Suivant</a>
        </li>
      </ul>
    </nav>

    <!-- Dialog d'ajout/modification (remplace le modal Bootstrap) -->
    <Dialog v-model:visible="projectDialogVisible" :style="{ width: '50vw' }" :breakpoints="{ '960px': '75vw', '641px': '90vw' }" modal>
      <template #header>
        <h5 class="modal-title">{{ isEditing ? 'Modifier' : 'Ajouter' }} une réalisation</h5>
      </template>
      
      <form @submit.prevent="saveProject">
        <div class="mb-3">
            <label class="form-label">Nom du projet <span class="text-danger">*</span></label>
            <input v-model="currentProject.name" type="text" class="form-control rounded-3" required>
        </div>

        <div class="mb-3">
          <label class="form-label">Description <span class="text-danger">*</span></label>
          <textarea v-model="currentProject.description" class="form-control rounded-3" rows="3"
            required></textarea>
        </div>

        <div class="row mb-3">
          <div class="col-md-6">
            <label class="form-label">Date de début <span class="text-danger">*</span></label>
            <input v-model="currentProject.startDate" type="date" class="form-control rounded-3" required>
          </div>
          <div class="col-md-6">
            <label class="form-label">Date de fin</label>
            <input v-model="currentProject.endDate" type="date" class="form-control rounded-3"
              :disabled="currentProject.isCurrent">
            <div class="form-check mt-2">
              <input v-model="currentProject.isCurrent" type="checkbox" class="form-check-input"
                id="currentProject">
              <label class="form-check-label" for="currentProject">En cours</label>
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-6">
            <label class="form-label">Statut</label>
            <select v-model="currentProject.status" class="form-select rounded-3">
              <option value="Terminé">Terminé</option>
              <option value="En cours">En cours</option>
              <option value="En pause">En pause</option>
              <option value="Annulé">Annulé</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="form-label">Lien (URL)</label>
            <input v-model="currentProject.link" type="url" class="form-control rounded-3" placeholder="https://">
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label">Compétences utilisées</label>
          <div class="d-flex align-items-center">
            <input v-model="skillInput" type="text" class="form-control rounded-3 me-2"
              placeholder="Ajouter une compétence" @keydown.enter.prevent="addSkill">
            <button type="button" class="btn btn-sm btn-outline-primary rounded-pill" @click="addSkill">
              <i class="fas fa-plus"></i>
            </button>
          </div>
          <div class="mt-2">
            <span class="badge bg-secondary me-1 mb-1 rounded-pill"
              v-for="(skill, index) in currentProject.skills" :key="index">
              {{ skill }} <i class="fas fa-times ms-1" @click="removeSkill(index)"></i>
            </span>
          </div>
        </div>

        <div v-if="currentProject.name">
          <image-uploader model-name="projects" :existing-image="currentProject.image_url"
            @image-uploaded="handleImageUploaded" />

          <div v-if="currentProject.image_url" class="mt-3">
            <img :src="currentProject.image_url" class="img-fluid" style="max-height: 300px;">
          </div>
        </div>
      </form>
      
      <template #footer>
        <button type="button" class="btn btn-outline-secondary rounded-pill me-2" @click="projectDialogVisible = false">
          Annuler
        </button>
        <button type="button" class="btn btn-primary rounded-pill" @click="saveProject">
          {{ isEditing ? 'Mettre à jour' : 'Enregistrer' }}
        </button>
      </template>
    </Dialog>

    <!-- Dialog de confirmation de suppression (remplace le modal Bootstrap) -->
    <Dialog v-model:visible="deleteDialogVisible" :style="{ width: '30vw' }" :breakpoints="{ '960px': '75vw', '641px': '90vw' }" modal>
      <template #header>
        <h5 class="modal-title">Confirmer la suppression</h5>
      </template>
      
      <p>Êtes-vous sûr de vouloir supprimer cette réalisation ? Cette action est irréversible.</p>
      
      <template #footer>
        <button type="button" class="btn btn-outline-secondary rounded-pill me-2" @click="deleteDialogVisible = false">
          Annuler
        </button>
        <button type="button" class="btn btn-danger rounded-pill" @click="deleteProject">
          Supprimer
        </button>
      </template>
    </Dialog>
  </div>
</template>

<script>
import Dialog from 'primevue/dialog';
import ImageUploader from '../../components/ImageUploader.vue';

export default {
  name: 'AchievementsView',
  components: { 
    Dialog,
    ImageUploader
  },
  data() {
    return {
      isLoading: false,
      searchQuery: '',
      sortOption: 'recent',
      currentPage: 1,
      itemsPerPage: 5,
      isEditing: false,
      currentProjectIndex: null,
      skillInput: '',
      currentProject: this.getEmptyProject(),
      projects: [],
      deleteIndex: null,
      // Variables pour contrôler la visibilité des dialogs
      projectDialogVisible: false,
      deleteDialogVisible: false
    }
  },
  computed: {
    filteredProjects() {
      let filtered = this.projects.filter(project =>
        project.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        project.description.toLowerCase().includes(this.searchQuery.toLowerCase())
      )

      // Tri
      if (this.sortOption === 'recent') {
        filtered.sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
      } else if (this.sortOption === 'oldest') {
        filtered.sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
      } else if (this.sortOption === 'name') {
        filtered.sort((a, b) => a.name.localeCompare(b.name))
      }

      // Pagination
      const start = (this.currentPage - 1) * this.itemsPerPage
      return filtered.slice(start, start + this.itemsPerPage)
    },
    totalPages() {
      return Math.ceil(this.projects.length / this.itemsPerPage)
    }
  },
  methods: {
    getMembershipDatas() {
      this.projects = this.$store.getters.membership.realisations ? this.$store.getters.membership.realisations : []
    },
    handleImageUploaded(imageUrl) {
      this.currentProject.image_url = imageUrl;
    },
    formatDate(dateString) {
      if (!dateString) return 'En cours'
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR')
    },
    statusClass(status) {
      const statusClasses = {
        'Terminé': 'bg-success',
        'En cours': 'bg-primary',
        'En pause': 'bg-warning',
        'Annulé': 'bg-danger'
      }
      return statusClasses[status] || 'bg-secondary'
    },
    statusIcon(status) {
      const statusIcons = {
        'Terminé': 'fa-check-circle',
        'En cours': 'fa-spinner',
        'En pause': 'fa-pause-circle',
        'Annulé': 'fa-times-circle'
      }
      return statusIcons[status] || 'fa-question-circle'
    },
    showAddModal() {
      this.isEditing = false
      this.currentProject = this.getEmptyProject()
      this.projectDialogVisible = true
    },
    editProject(index) {
      this.isEditing = true
      this.currentProjectIndex = index
      this.currentProject = JSON.parse(JSON.stringify(this.filteredProjects[index]))
      this.projectDialogVisible = true
    },
    saveProject() {
      if (this.isEditing) {
        const projectId = this.filteredProjects[this.currentProjectIndex].id
        const mainIndex = this.projects.findIndex(p => p.id === projectId)
        this.projects.splice(mainIndex, 1, this.currentProject)
      } else {
        const maxId = Math.max(...this.projects.map(p => p.id), 0)
        this.currentProject.id = maxId + 1
        this.projects.unshift(this.currentProject)
      }
      this.projectDialogVisible = false
      this.saveSection()
    },
    confirmDelete(index) {
      this.deleteIndex = index
      this.deleteDialogVisible = true
    },
    deleteProject() {
      const projectId = this.filteredProjects[this.deleteIndex].id
      const mainIndex = this.projects.findIndex(p => p.id === projectId)
      this.projects.splice(mainIndex, 1)
      this.deleteIndex = null
      this.deleteDialogVisible = false

      this.saveSection()

      if (this.currentPage > 1 && this.filteredProjects.length === 0) {
        this.currentPage--
      }
    },
    downloadProject(index) {
      const project = this.filteredProjects[index]
      alert(`Téléchargement du projet: ${project.name}`)
      // Ici vous implémenteriez la logique de téléchargement réelle
    },
    addSkill() {
      if (this.skillInput && !this.currentProject.skills.includes(this.skillInput)) {
        this.currentProject.skills.push(this.skillInput)
        this.skillInput = ''
      }
    },
    removeSkill(index) {
      this.currentProject.skills.splice(index, 1)
    },
    handleFileUpload(event) {
      const files = event.target.files
      for (let i = 0; i < files.length; i++) {
        this.currentProject.files.push(files[i].name)
      }
    },
    getEmptyProject() {
      return {
        id: null,
        name: '',
        icon: 'fa-project-diagram',
        description: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        isCurrent: false,
        status: 'Terminé',
        link: '',
        skills: [],
        files: []
      }
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage--
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++
    },
    goToPage(page) {
      this.currentPage = page
    },
    async saveSection() {
      this.isLoading = true

      const membership = this.$store.getters.membership
      let form = {
        photo: membership.photo ? membership.photo : null,
        name: membership.nom_complet ? membership.nom_complet : null,
        job: membership.titre_pro ? membership.titre_pro : null,
        email: membership.email ? membership.email : null,
        phone: membership.telephone ? membership.telephone : null,
        birthdate: membership.date_naissance ? membership.date_naissance : null,
        nationality: membership.nationalite ? membership.nationalite : null,
        maritalStatus: membership.situation_matri ? membership.situation_matri : null,
        bio: membership.bio ? membership.bio : null,
        address: membership.adresse ? membership.adresse : null,
        address2: membership.adresse_complement ? membership.adresse_complement : null,
        postalCode: membership.bp ? membership.bp : null,
        city: membership.ville ? membership.ville : null,
        country: membership.pays ? membership.pays : null,
        experiences: membership.experiences_pro ? membership.experiences_pro : [],
        educations: membership.formations ? membership.formations : [],
        skills: membership.competences ? membership.competences : [],
        languages: membership.langues ? membership.langues : [],
        certifications: membership.certifications ? membership.certifications : [],
        realisations: this.projects
      }

      await this.axios.post(`/api/membership/${this.$store.getters.membership.id}/update`, form).then(response => {
        this.isLoading = false
        if (response.data.success === true) {
          this.$store.commit('setMembership', response.data.data);
          this.$toast.add({
            severity: 'success',
            detail: 'Enregistrement réussie',
            life: 3000
          });
        } else {
          const errors = response.data.errors || ['Une erreur est survenue'];
          if (Array.isArray(errors)) {
            errors.forEach(element => {
              this.$toast.add({
                severity: 'warn',
                summary: 'Oups !',
                detail: element,
                life: 7000
              });
            });
          }
        }
      }).catch(error => {
        this.isLoading = false
        this.$toast.add({
          severity: 'error',
          summary: 'Problème de connexion',
          detail: 'Une erreur s\'est produite lors de la connexion au serveur !',
          life: 5000
        });
      })
    },
  },
  mounted() {
    this.getMembershipDatas()
  }
}
</script>

<style scoped>
.achievements-container {
  max-width: 1200px;
  margin: 0 auto;
}

.project-card {
  transition: all 0.3s;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.project-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.project-image {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
}

.project-actions {
  opacity: 0;
  transition: opacity 0.3s;
}

.project-card:hover .project-actions {
  opacity: 1;
}

.badge {
  padding: 0.5em 0.8em;
  font-weight: 500;
}

.page-link {
  margin: 0 3px;
  border-radius: 0.5rem !important;
}

.modal-content {
  border: none;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
}

.badge .fa-times {
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.badge .fa-times:hover {
  opacity: 1;
}

.p-dialog-header {
  padding: 1.25rem 1.5rem 0.5rem !important;
}

</style>